import React from "react";
import { motion } from "framer-motion";
import { Search, Map, Rocket, ArrowRight } from "lucide-react";

const CALENDLY_URL = "https://cal.com/mindways/discovery-call";

const steps = [
  {
    number: "01",
    icon: Search,
    title: "Assess",
    subtitle: "Clarity Session",
    bullets: [
      "Structured student & parent evaluation",
      "Profile audit and gap analysis",
      "Goal and constraints mapping",
    ],
    color: "#EEF0FC",
    iconColor: "#2B2F9E",
  },
  {
    number: "02",
    icon: Map,
    title: "Strategise",
    subtitle: "Build Your Roadmap",
    bullets: [
      "Course and country pathway planning",
      "Test plan and timeline structuring",
      "University shortlist and application calendar",
    ],
    color: "#FFF7E6",
    iconColor: "#F5A000",
  },
  {
    number: "03",
    icon: Rocket,
    title: "Execute",
    subtitle: "Applications & Beyond",
    bullets: [
      "SOP, CV, and LOR strategy and reviews",
      "Application checks and submissions",
      "Visa guidance and pre-departure readiness",
    ],
    color: "#F0FDF4",
    iconColor: "#10B981",
  },
];

export default function Process({ onBookCall }) {
  return (
    <section
      id="process"
      data-testid="process-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: "center", marginBottom: "64px" }}
        >
          <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Our Approach
          </span>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: "700",
            color: "#0F172A",
            margin: "12px 0 16px",
          }}>
            A simple process that keeps you in control
          </h2>
          <p style={{ fontSize: "17px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", maxWidth: "540px", margin: "0 auto" }}>
            Three clear stages. Each with accountability, reviews, and senior oversight.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="mw-grid-3" style={{ gap: "32px", position: "relative" }}>

          {/* Connector line */}
          <div style={{
            position: "absolute",
            top: "60px",
            left: "calc(33.33% - 20px)",
            right: "calc(33.33% - 20px)",
            height: "2px",
            backgroundColor: "#E2E8F0",
            zIndex: 0,
            display: "none",
          }}
            className="hidden md:block"
          />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                data-testid={`process-step-${i + 1}`}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "40px 32px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  position: "relative",
                  zIndex: 1,
                  transition: "box-shadow 0.3s",
                }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }}
              >
                {/* Step number */}
                <div style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#F1F5F9",
                  fontFamily: "'Playfair Display', serif",
                  position: "absolute",
                  top: "20px",
                  right: "24px",
                  lineHeight: "1",
                }}>
                  {step.number}
                </div>

                {/* Icon */}
                <div style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "12px",
                  backgroundColor: step.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}>
                  <Icon size={24} style={{ color: step.iconColor }} />
                </div>

                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#0F172A",
                  margin: "0 0 4px",
                }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: "13px", color: step.iconColor, fontFamily: "'DM Sans', sans-serif", fontWeight: "600", margin: "0 0 20px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {step.subtitle}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {step.bullets.map((b) => (
                    <div key={b} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: step.iconColor, marginTop: "7px", flexShrink: 0 }} />
                      <span style={{ fontSize: "14px", color: "#475569", fontFamily: "'DM Sans', sans-serif", lineHeight: "1.5" }}>{b}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          style={{ textAlign: "center", marginTop: "48px" }}
        >
          <button
            data-testid="process-book-call-btn"
            onClick={() => window.open(CALENDLY_URL, "_blank")}
            style={{
              backgroundColor: "#2B2F9E",
              color: "#fff",
              border: "none",
              padding: "16px 36px",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: "700",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: "0 8px 24px rgba(43,47,158,0.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(43,47,158,0.35)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(43,47,158,0.25)"; }}
          >
            Book a Strategy Call <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
